import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings, X } from "lucide-react";

const STORAGE_KEY = "ae_cookie_consent_v1";

const defaultPrefs = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setOpen(true);
      } else {
        setPrefs(JSON.parse(raw));
      }
    } catch (e) {
      setOpen(true);
    }
  }, []);

  const persist = (next) => {
    const payload = { ...next, timestamp: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setPrefs(payload);
    setOpen(false);
    setShowSettings(false);
    // Broadcast event so analytics scripts can react
    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: payload }));
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const saveSelection = () => persist(prefs);

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-5"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Einwilligung"
    >
      <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-2xl">
        {!showSettings ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Cookie className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900">
                  Wir verwenden Cookies
                </h2>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                  Wir nutzen technisch notwendige Cookies, damit unsere Seite funktioniert.
                  Mit deiner Einwilligung verwenden wir zusätzlich Cookies für Statistik
                  und Marketing, um unser Angebot zu verbessern. Du kannst deine Auswahl
                  jederzeit anpassen. Mehr Infos in unserer{" "}
                  <Link to="/datenschutz" className="text-blue-600 hover:underline">
                    Datenschutzerklärung
                  </Link>{" "}
                  und im{" "}
                  <Link to="/cookie" className="text-blue-600 hover:underline">
                    Cookie-Hinweis
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
              <button
                data-testid="cookie-settings-btn"
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Settings className="h-4 w-4" />
                Einstellungen
              </button>
              <button
                data-testid="cookie-reject-btn"
                onClick={rejectAll}
                className="px-4 py-2.5 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Nur notwendige
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={acceptAll}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900">
                Cookie-Einstellungen
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                aria-label="Schließen"
                data-testid="cookie-settings-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Wähle, welche Cookies du zulassen möchtest. Technisch notwendige Cookies
              sind für den Betrieb der Seite erforderlich.
            </p>

            <div className="mt-4 space-y-3">
              <CookieRow
                title="Notwendig"
                description="Erforderlich für grundlegende Funktionen (z. B. Formulare, Sitzung)."
                checked
                disabled
              />
              <CookieRow
                title="Statistik"
                description="Anonyme Auswertung, damit wir die Seite verbessern können."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="cookie-analytics-toggle"
              />
              <CookieRow
                title="Marketing"
                description="Hilft uns, relevante Inhalte und Angebote anzuzeigen."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="cookie-marketing-toggle"
              />
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
              <button
                data-testid="cookie-reject-btn-2"
                onClick={rejectAll}
                className="px-4 py-2.5 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Nur notwendige
              </button>
              <button
                data-testid="cookie-save-btn"
                onClick={saveSelection}
                className="px-4 py-2.5 text-sm font-medium text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
              >
                Auswahl speichern
              </button>
              <button
                data-testid="cookie-accept-all-btn-2"
                onClick={acceptAll}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieRow({ title, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <p className="text-xs text-slate-600 mt-0.5">{description}</p>
      </div>
      <label className="inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
          data-testid={testId}
        />
        <div
          className={`relative w-11 h-6 rounded-full transition-colors ${
            disabled
              ? "bg-blue-300 cursor-not-allowed"
              : checked
              ? "bg-blue-600"
              : "bg-slate-300"
          } peer-focus:ring-2 peer-focus:ring-blue-200`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              checked ? "translate-x-5" : ""
            }`}
          />
        </div>
      </label>
    </div>
  );
}

export function openCookieSettings() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}
